import { useFreeBuyBonusStore } from '@stores/freeBuyBonusStore'
import { useCountdownFSBonusStore, useFreeSpinsBonusesStore, useLuckySpinsStore } from '@stores'
import React, { useEffect } from 'react'
import errorImage from '@assets/bonus-system/alert_4.png'
import recoveryImage from '@assets/bonus-system/icon_InfoLine.png'
import cfsRecoveryImage from '@assets/bonus-system/icon_InfoLine_countdown.png'
import recoveryBg from '@assets/bonus-system/free-spin-mode-notification-background.png'
import { useTranslation } from '@amusnet/translations/react'

// TODO: use react ui implementation of notifications
const GenericNotification: React.FC = () => {
    const { t } = useTranslation()

    const isThereFBBClaimingError = useFreeBuyBonusStore((state) => state.isThereClaimingError)
    const setIsThereFBBClaimingError = useFreeBuyBonusStore((state) => state.setIsThereClaimingError)
    const setIsFBBonusAlreadyClaimed = useFreeBuyBonusStore((state) => state.setIsBonusAlreadyClaimed)
    const isFBBonusAlreadyClaimed = useFreeBuyBonusStore((state) => state.isBonusAlreadyClaimed)

    const isThereBonusSpinsClaimingError = useFreeSpinsBonusesStore((state) => state.isThereClaimingError)
    const setIsThereBonusSpinsClaimingError = useFreeSpinsBonusesStore((state) => state.setIsThereClaimingError)
    const setIsFSBonusAlreadyClaimed = useFreeSpinsBonusesStore((state) => state.setIsBonusAlreadyClaimed)
    const isFSBonusAlreadyClaimed = useFreeSpinsBonusesStore((state) => state.isBonusAlreadyClaimed)
    const isFSRecovery = useFreeSpinsBonusesStore((state) => state.isRecovery)
    const setIsFSRecovery = useFreeSpinsBonusesStore((state) => state.setIsRecovery)

    const isThereCFSClaimingError = useCountdownFSBonusStore((state) => state.isThereClaimingError)
    const setIsThereCFSClaimingError = useCountdownFSBonusStore((state) => state.setIsThereClaimingError)
    const setIsCFSBonusAlreadyClaimed = useCountdownFSBonusStore((state) => state.setIsBonusAlreadyClaimed)
    const isCFSBonusAlreadyClaimed = useCountdownFSBonusStore((state) => state.isBonusAlreadyClaimed)
    const isStoreCFSRecovery = useCountdownFSBonusStore((state) => state.isRecovery)
    const hasActiveCFSBonusExpired = useCountdownFSBonusStore((state) => state.hasActiveBonusExpired)
    const setIsCFSRecovery = useCountdownFSBonusStore((state) => state.setIsRecovery)

    const isLSRecovery = useLuckySpinsStore((state) => state.isRecovery)
    const setIsLSRecovery = useLuckySpinsStore((state) => state.setIsRecovery)

    const isThereClaimingError = isThereBonusSpinsClaimingError || isThereFBBClaimingError || isThereCFSClaimingError
    const isBonusAlreadyClaimed = isFSBonusAlreadyClaimed || isFBBonusAlreadyClaimed || isCFSBonusAlreadyClaimed
    const isCFSRecovery = isStoreCFSRecovery && !hasActiveCFSBonusExpired()
    const isRecovery = isFSRecovery || isLSRecovery || isCFSRecovery
    // eslint-disable-next-line no-undef
    let timer: NodeJS.Timeout

    useEffect(() => {
        if (isThereClaimingError || isBonusAlreadyClaimed) {
            timer = setTimeout(() => {
                setIsThereBonusSpinsClaimingError(false)
                setIsThereFBBClaimingError(false)
                setIsThereCFSClaimingError(false)
                setIsFBBonusAlreadyClaimed(false)
                setIsFSBonusAlreadyClaimed(false)
                setIsCFSBonusAlreadyClaimed(false)
            }, 3000)
        }

        return () => clearTimeout(timer)
    }, [isThereClaimingError, isBonusAlreadyClaimed])

    useEffect(() => {
        if (isRecovery) {
            timer = setTimeout(() => {
                setIsFSRecovery(false)
                setIsLSRecovery(false)
                setIsCFSRecovery(false)
            }, 3000)
        }

        return () => clearTimeout(timer)
    }, [isRecovery])

    if (!isThereClaimingError && !isBonusAlreadyClaimed && !isRecovery) {
        return
    }

    const warningMessageContent =
        isThereClaimingError && !isBonusAlreadyClaimed
            ? t('gameui.bonusSystem.somethingWentWrongError')
            : t('gameui.bonusSystem.alreadyClaimedError')

    const recoveryContent = () => {
        if (isCFSRecovery)
            return (
                <span>
                    {`${t('gameui.bonusSystem.countdownFreeSpinsRecoveryPrefix') ?? 'Your'}`}{' '}
                    <span className="font-bold">{` ${t('gameui.bonusSystem.countdownFreeSpinsTitle')} `}</span>{' '}
                    {`${t('gameui.bonusSystem.countdownFreeSpinsRecoverySuffix') ?? 'are still active.'} `}
                </span>
            )

        if (isFSRecovery)
            return (
                <span>
                    {`${t('gameui.bonusSystem.freeSpinsRecoveryPrefix') ?? 'Your'}`}{' '}
                    <span className="font-bold">{` ${t('gameui.bonusSystem.freeSpinsRecoveryPrefix') ? t('gameui.bonusSystem.freeSpinsTitle') : 'FREE SPIN MODE'} `}</span>{' '}
                    {`${t('gameui.bonusSystem.freeSpinsRecoverySuffix') ?? 'is still active!'} `}
                </span>
            )

        return (
            <span>
                {`${t('gameui.bonusSystem.luckySpinsRecoveryPrefix') ?? 'You still have remaining'}`}{' '}
                <span className="font-bold">{` ${t('gameui.bonusSystem.luckySpin', undefined, 2)}`}</span>{' '}
                {`${t('gameui.bonusSystem.luckySpinsRecoverySuffix') ?? '.'} `}
            </span>
        )
    }

    const notificationContent = () => {
        if (isRecovery)
            return (
                <>
                    {recoveryContent()}
                    <span className="text-[30px]">{` ${t('gameui.goodLuck') || 'GOOD LUCK!'}`}</span>
                </>
            )

        return <span>{`${t('gameui.oops')} ${warningMessageContent}`}</span>
    }

    const styles = {
        image: isRecovery ? (isCFSRecovery ? cfsRecoveryImage : recoveryImage) : errorImage,
        background: isRecovery
            ? `url(${recoveryBg})`
            : 'linear-gradient(#1E2024, #1E2024) padding-box, linear-gradient(180deg, #F9C246 0%, #F9E746 47.92%, #F9C246 100%) border-box',
        border: isRecovery ? undefined : '3px solid transparent',
    }

    return (
        <div
            className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[140px] bg-[#1E2024] text-white flex items-center justify-center px-[25px] z-[9999] mt-[5px] rounded-2xl"
            style={{
                background: styles.background,
                border: styles.border,
                borderRadius: '16px',
            }}
        >
            <img className="h-[76px]" src={styles.image} alt="Bonus image" />

            <div className="text-left text-[30px] pl-[20px]">{notificationContent()}</div>
        </div>
    )
}

export default GenericNotification
